"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RecentTransformations() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const galleryImages = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/freshly_mowed_lawn_stripes.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/freshly_mowed_lawn_stripes.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/freshly_mowed_lawn_stripes.png-wvc-sizes",
    alt: "Perfectly mowed lawn with clean stripes",
    caption: "MOW.01 / PRECISION STRIPES",
    aspect: "aspect-[3/4]",
    colSpan: "md:col-span-4 md:row-span-2"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/sharp_concrete_edging.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/sharp_concrete_edging.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/sharp_concrete_edging.png-wvc-sizes",
    alt: "Sharp concrete edging along a walkway",
    caption: "EDGE.04 / THE CLEAN LINE",
    aspect: "aspect-[16/10]",
    colSpan: "md:col-span-5"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/trimmed_shrubbery_detail.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/trimmed_shrubbery_detail.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/trimmed_shrubbery_detail.png-wvc-sizes",
    alt: "Close up of perfectly trimmed ornamental shrubs",
    caption: "TRIM.02 / SCULPTED FORM",
    aspect: "aspect-square",
    colSpan: "md:col-span-3"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/morning_dew_on_grass.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/morning_dew_on_grass.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/morning_dew_on_grass.png-wvc-sizes",
    alt: "Morning dew on vibrant green grass blades",
    caption: "TURF.08 / MORNING DEW",
    aspect: "aspect-[3/4]",
    colSpan: "md:col-span-3 md:row-span-2"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/commercial_mower_action.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/commercial_mower_action.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/commercial_mower_action.png-wvc-sizes",
    alt: "Professional mower in action on a large field",
    caption: "EQUIP.01 / BLADE ARCHITECTURE",
    aspect: "aspect-[5/4]",
    colSpan: "md:col-span-5"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/manicured_garden_border.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/manicured_garden_border.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1696120/img/manicured_garden_border.png-wvc-sizes",
    alt: "Manicured garden border with mulch and edging",
    caption: "FINISH.03 / THE SPEC",
    aspect: "aspect-square",
    colSpan: "md:col-span-4"
  }];
  return <section data-section-id="88" id="recent-transformations" className="bg-background text-foreground py-20 md:py-32 lg:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 mb-16 md:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="md:col-span-4 lg:col-span-3">
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-primary">
              02 / Visual Prelude [MOW.01]
            </span>
          </div>
          <div className="md:col-span-8 lg:col-span-9 md:col-start-5 lg:col-start-4">
            <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tighter font-extrabold uppercase">
              <span>Precision</span>
              <span className="inline-block mx-2 md:mx-3 text-primary">/</span>
              <br className="hidden md:block" />
              <span>The Perfect Cut</span>
            </h2>
          </div>
        </div>

        {}
        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-12 md:auto-rows-[180px] lg:auto-rows-[220px] gap-4 md:gap-5 transition-all duration-1000 ease-out delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {galleryImages.map((image, i) => <div key={i} data-index={i} className={`group relative overflow-hidden rounded-md border border-border/40 shadow-md ${image.colSpan} ${image.aspect} md:aspect-auto bg-muted`}>
              <img src={image.src} data-wvc-srcset={image.srcset} data-wvc-sizes={image.sizes} alt={image.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.04]" />
              {}
              <div className="absolute inset-x-0 bottom-0 overflow-hidden">
                <div className="translate-y-full group-hover:translate-y-0 transition-transform duration-700 ease-out">
                  <div className="px-4 py-3 md:px-5 md:py-4 backdrop-blur-sm bg-foreground/80">
                    <span className="font-mono text-[10px] md:text-xs tracking-[0.2em] uppercase text-primary">
                      {image.caption}
                    </span>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {}
        <div ref={ctaRef} className={`mt-12 md:mt-16 flex justify-end transition-all duration-1000 ease-out delay-300 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <Button asChild variant="ghost" className="group h-auto px-0 py-2 text-foreground hover:bg-transparent hover:text-primary">
            <Link to="/GetQuotePage" className="inline-flex items-center gap-3">
              <span className="font-default text-lg md:text-xl font-bold uppercase border-b-2 border-primary pb-1 transition-colors duration-500">
                View our full portfolio
              </span>
              <ArrowRight className="size-4 transition-transform duration-500 group-hover:translate-x-1 text-primary" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}